import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Button } from "../../components/ui";
import { reintentarTarea } from "./api";
import {
  TITULO_TAREA,
  invalidarTrasTarea,
  marcarPorAvisar,
  porcentajeAvance,
  registrarTareaLanzada,
  resumenTarea,
  textoAvance,
  tomarAviso,
  useTareaCierre,
} from "./tareas";

/**
 * Avance de la operación larga del cierre (estimar, calcular, emitir).
 *
 * Corre en el servidor: el operador puede cerrar el diálogo, seguir
 * trabajando o salir; al volver al cierre la barra sigue acá. Al terminar
 * refresca el cierre y avisa; si falla muestra el motivo y deja reintentar.
 */
export function TareaCierrePanel({ cierreId }: { cierreId: number }): JSX.Element | null {
  const qc = useQueryClient();
  const toast = useToast();
  const tareaQ = useTareaCierre(cierreId);
  const tarea = tareaQ.data ?? null;
  // Fallida descartada por el operador (sólo en esta visita).
  const [descartada, setDescartada] = useState<number | null>(null);

  // Sólo avisa el final de una tarea que esta pestaña lanzó o vio correr,
  // no el de una vieja que ya estaba terminada al entrar.
  useEffect(() => {
    if (!tarea) return;
    if (tarea.estado === "en_curso") {
      marcarPorAvisar(tarea.id);
      return;
    }
    if (!tomarAviso(tarea.id)) return;
    invalidarTrasTarea(qc, cierreId);
    if (tarea.estado === "terminada") toast.success(resumenTarea(tarea));
    else toast.error(tarea.error ?? "La operación no se pudo completar");
  }, [tarea, qc, cierreId, toast]);

  const reintentar = useMutation({
    mutationFn: reintentarTarea,
    onSuccess: (nueva) => registrarTareaLanzada(qc, nueva),
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "No se pudo reintentar");
      void tareaQ.refetch();
    },
  });

  if (!tarea) return null;

  if (tarea.estado === "en_curso") {
    const pct = porcentajeAvance(tarea);
    const texto = textoAvance(tarea);
    return (
      <section
        className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 space-y-2"
        aria-labelledby="tarea-cierre-titulo"
      >
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-brand-700" aria-hidden="true" />
          <h2 id="tarea-cierre-titulo" className="text-sm font-semibold text-neutral-900">
            {TITULO_TAREA[tarea.tipo]} en curso
          </h2>
        </div>
        <div
          role="progressbar"
          aria-label={texto}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          className="h-2 w-full overflow-hidden rounded-full bg-white ring-1 ring-brand-200"
        >
          <div
            className="h-full rounded-full bg-brand-700 transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-sm text-neutral-700 tabular-nums" aria-live="polite">
          {texto}
        </p>
        <p className="text-xs text-neutral-600">
          Puedes seguir trabajando o salir de esta página; el avance queda aquí.
        </p>
      </section>
    );
  }

  if (tarea.estado === "fallida" && descartada !== tarea.id) {
    return (
      <section
        role="alert"
        className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 space-y-2"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-rose-700"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-rose-900">
              {TITULO_TAREA[tarea.tipo]}: no se completó
            </h2>
            <p className="text-sm text-rose-800">
              {tarea.error ?? "La operación no se pudo completar."} No se guardó ningún
              cambio de esta operación.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            icon={RotateCcw}
            disabled={reintentar.isPending}
            onClick={() => reintentar.mutate(tarea)}
          >
            {reintentar.isPending ? "Reintentando…" : "Reintentar"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDescartada(tarea.id)}>
            Descartar
          </Button>
        </div>
      </section>
    );
  }

  return null;
}
